import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { JOB_TITLE_CODE_PATTERN, NAME_MAX } from './hrApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  PAY_COMPONENT_KINDS,
  createPayComponent,
  updatePayComponent,
  type PayComponent,
  type PayComponentKind,
} from './payApi'

interface Props {
  open: boolean
  /** `null` — yangi qism */
  component: PayComponent | null
  onClose: () => void
}

interface FormValues {
  code: string
  name: string
  kind: PayComponentKind
  recurring: boolean
  active: boolean
}

/**
 * Ish haqi qismi — katalog yozuvi.
 *
 * <h2>⚠️ YO'NALISH RO'YXATI QO'LDA YOZILMAYDI</h2>
 * Variantlar `PAY_COMPONENT_KINDS` dan olinadi va u Java enumi bilan
 * sinovda solishtiriladi (`tests/hr-pay.mjs` §8). Bu yerga
 * `['EARNING', 'DEDUCTION']` deb qo'lda yozish AYNAN 2026-08-14 da
 * `subscriber-movement` da chiqqan nuqson bo'lardi: ro'yxat backend
 * bilan ajralib ketadi va panel bazada mavjud bo'lmagan qiymatni
 * jimgina yuborib turadi.
 *
 * <h2>⚠️ TIZIM QISMIDA UCH MAYDON QULFLANADI — VA SABABI O'LCHANGAN</h2>
 * `hr.pay_component_system_guard()` tizim qatorida (`BASE`) AYNAN
 * to'rt narsani rad etadi: `active` ni o'chirish, `is_base`, `kind`
 * va `code` ni o'zgartirish. `name` esa O'ZGARADI — ya'ni «tizim
 * qatorini umuman tahrirlash mumkin emas» degan xulosa noto'g'ri
 * bo'lardi va operator nomni tuzata olmasdi.
 *
 * <p>⚠️ `recurring` ham qulflanadi, lekin BOSHQA sabab bilan:
 * `pay_component_base_shape` asosiy qismdan `is_recurring` ni talab
 * qiladi. Qo'riqchi buni ko'rmaydi, `CHECK` esa `23514` beradi —
 * ya'ni tugma ishlagandek ko'rinib, saqlashda yiqilardi.
 *
 * <h2>⚠️ O'CHIRISH YO'Q</h2>
 * Backendda `DELETE` umuman yo'q. Qism o'chirilsa unga tayangan
 * maosh satrlari va tasdiqlangan varaqalar nomsiz qolardi. O'rniga
 * «Faol» o'chiriladi: eski satrlar joyida qoladi, yangisi esa bu
 * qismga yozilmaydi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * Yopishda `save.reset()`.
 */
export function PayComponentFormModal({ open, component, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const locked = component?.system ?? false

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      code: component?.code ?? '',
      name: component?.name ?? '',
      kind: component?.kind ?? 'EARNING',
      recurring: component?.recurring ?? true,
      active: component?.active ?? true,
    })
  }, [open, component, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        kind: values.kind,
        recurring: values.recurring,
        active: values.active,
      }
      return component === null
        ? createPayComponent(draft)
        : updatePayComponent(component.id, draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={
        component === null ? t('hr.payComponent.createTitle') : t('hr.payComponent.editTitle')
      }
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.payComponent.note')} fontSize={13} />
      </div>

      {locked ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.payComponent.systemLocked')}
        />
      ) : null}

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="code"
          label={t('hr.payComponent.code')}
          extra={t('hr.payComponent.codeHint')}
          rules={[
            { required: true, message: t('hr.payComponent.validation.codeRequired') },
            {
              validator: (_rule, value: string | undefined) =>
                !value || JOB_TITLE_CODE_PATTERN.test(value.trim().toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('hr.payComponent.validation.codeFormat'))),
            },
          ]}
        >
          <Input autoFocus disabled={locked} />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('hr.payComponent.name')}
          rules={[
            { required: true, message: t('hr.payComponent.validation.nameRequired') },
            { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="kind"
          label={t('hr.payComponent.kind')}
          extra={t('hr.payComponent.kindHint')}
          rules={[{ required: true, message: t('hr.payComponent.validation.kindRequired') }]}
        >
          <Select<PayComponentKind>
            disabled={locked}
            options={PAY_COMPONENT_KINDS.map((value) => ({
              value,
              label: t(`payComponentKind.${value}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="recurring"
          label={t('hr.payComponent.recurring')}
          extra={t('hr.payComponent.recurringHint')}
          valuePropName="checked"
        >
          <Switch disabled={locked} />
        </Form.Item>

        <Form.Item
          name="active"
          label={t('hr.payComponent.active')}
          extra={t('hr.payComponent.activeHint')}
          valuePropName="checked"
        >
          <Switch disabled={locked} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
